import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Handshake, Shield, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { formatEUR } from "../lib/format";

export default function TariffarioPage() {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/tariffs").then((r) => setTiers(r.data?.tiers || []));
  }, []);

  return (
    <div className="py-6 md:py-12 max-w-4xl" data-testid="tariffario-page">
      <Link
        to="/home"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("common.back")}
      </Link>

      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
        {t("tariff.overline")}
      </p>
      <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
        {t("tariff.title")}
      </h1>
      <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
        {t("tariff.subtitle")}
      </p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-10 rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-sm"
        data-testid="tariff-table"
      >
        <div className="grid grid-cols-2 px-6 py-4 text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold border-b border-slate-100 bg-slate-50/60">
          <span>{t("tariff.col_range")}</span>
          <span className="text-right">{t("tariff.col_commission")}</span>
        </div>
        {tiers.map((tier, i) => (
          <div
            key={i}
            data-testid={`tariff-tier-${tier.tier}`}
            className="grid grid-cols-2 px-6 py-5 items-center border-b border-slate-100 last:border-b-0"
          >
            <div>
              <div className="font-heading font-semibold text-slate-950 text-base">
                {tier.label}
              </div>
              {tier.custom && (
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <Sparkles className="w-2.5 h-2.5" />
                  {t("tariff.custom_flag")}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="font-heading font-bold text-2xl tabular-nums text-slate-950">
                {formatEUR(tier.commission_eur)}
              </div>
              <div className="text-xs text-slate-500">{t("tariff.per_deal")}</div>
            </div>
          </div>
        ))}
      </motion.div>

      <div className="mt-8 rounded-2xl bg-slate-950 text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-300 mb-3">
            <Handshake className="w-3.5 h-3.5" /> {t("tariff.model_overline")}
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight max-w-xl">
            {t("tariff.model_title")}
          </h2>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed max-w-2xl">
            {t("tariff.model_desc")}
          </p>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <PillarCard
          icon={Check}
          title={t("tariff.pillar1_title")}
          desc={t("tariff.pillar1_desc")}
        />
        <PillarCard
          icon={Shield}
          title={t("tariff.pillar2_title")}
          desc={t("tariff.pillar2_desc")}
        />
      </div>
    </div>
  );
}

function PillarCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="mt-3 font-heading font-semibold text-base tracking-tight">
        {title}
      </h3>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}
